/**
 * Block 12 PR-Hotfix-2B Phase B3 Step 5 — SectionCustomRows (Свои позиции).
 *
 * Closes Audit Round 2 Bug 9 — section UI полностью отсутствовала (state
 * customRows был hardcoded `[]` в Container). Renders list of customRows +
 * inline «+ Добавить позицию» dashed button + collapsible add form per
 * прототип 2.12.2:527-572.
 *
 * **PR-H1 #3 (2026-05-26):** click-to-edit для existing rows. Pencil icon
 * между ценой и trash → click expand inline form (тот же `<CustomRowForm>`
 * sub-component) с pre-filled values. Save → dispatch UPDATE_CUSTOM_ROW (action
 * existed в reducer но UI affordance отсутствовала, line 403-411 anketaReducer).
 *
 * **Источники истины (Hybrid per Aibek decision 2026-05-16):**
 * - UNIT_OPTIONS — прототип 2.12.2:204 + consistency across 2.12.{3,4,7,8,9}
 *   (`['шт','м','м²','лист','компл.','час','другое']`) — НЕ TZ list (TZ был
 *   `['шт','м','м²','м³','кг','час']` — diverged from canonical prototype).
 * - Validation — TZ rule `name.trim() && qty > 0` (submit disabled). Прототип
 *   делал silent no-op (только name.trim) — TZ-rule gives users explicit
 *   feedback.
 * - UI — прототип chip-button row for unit selector (better touch target
 *   на mobile vs <select>).
 *
 * **State integration:** customRows хранятся в `AnketaState.customRows:
 * MaterialRow[]` (reducer actions ADD_CUSTOM_ROW / UPDATE_CUSTOM_ROW /
 * REMOVE_CUSTOM_ROW уже existing). Cost field computed caller-side (qty × price)
 * перед dispatch; reducer не пересчитывает. selectCustomRowsCost фильтрует
 * `enabled !== false`.
 *
 * **Section header conditional:** «Свои позиции» card renders только when
 * `rows.length > 0` (matches прототип 2.12.2:527 `{customRows.length > 0 &&}`).
 * Empty state — только «+ Добавить позицию» button visible.
 */

import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import type { MaterialRow } from '../types';

/** UNIT_OPTIONS per прототип 2.12.2:204 + consistent across all 6 anketa prototypes. */
const CUSTOM_ROW_UNITS = ['шт', 'м', 'м²', 'лист', 'компл.', 'час', 'другое'] as const;
type CustomRowUnit = (typeof CUSTOM_ROW_UNITS)[number];

export interface SectionCustomRowsProps {
  rows: MaterialRow[];
  onAdd: (row: MaterialRow) => void;
  onUpdate: (rowId: string, patch: Partial<MaterialRow>) => void;
  onRemove: (rowId: string) => void;
}

interface DraftState {
  name: string;
  qty: number;
  unit: CustomRowUnit;
  price: number;
}

const INITIAL_DRAFT: DraftState = { name: '', qty: 1, unit: 'шт', price: 0 };

function rowToDraft(row: MaterialRow): DraftState {
  return {
    name: row.name,
    qty: row.qty,
    unit: (CUSTOM_ROW_UNITS as readonly string[]).includes(row.unit)
      ? (row.unit as CustomRowUnit)
      : 'шт',
    price: row.price,
  };
}

interface CustomRowFormProps {
  initial: DraftState;
  titleText: string;
  submitText: string;
  /** Full testid для form контейнера (e.g. "custom-row-add-form", "custom-row-{id}-edit-form"). */
  formTestId: string;
  /** Prefix для child inputs/buttons (e.g. "custom-row" → `custom-row-name-input`). */
  fieldTestIdPrefix: string;
  onSubmit: (draft: DraftState) => void;
  onCancel: () => void;
}

/**
 * PR-H1 #3 — extracted form shared между «Новая позиция» (add) и «Редактирование»
 * (edit existing row). Single source of truth для UI + validation rules.
 * Two separate testId props сохраняют существующие test IDs для add case
 * (custom-row-{name|qty|...}-input) и дают unique IDs для edit case.
 */
function CustomRowForm({
  initial,
  titleText,
  submitText,
  formTestId,
  fieldTestIdPrefix,
  onSubmit,
  onCancel,
}: CustomRowFormProps) {
  const [draft, setDraft] = useState<DraftState>(initial);
  const isValid = draft.name.trim().length > 0 && draft.qty > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({ ...draft, name: draft.name.trim() });
  };

  return (
    <div
      className="bg-card border-[1.5px] border-cta rounded-2xl p-4 flex flex-col gap-3"
      data-testid={formTestId}
    >
      <h4 className="text-ink font-bold text-lg m-0">{titleText}</h4>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-ink">Название</span>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Например: Карниз"
          aria-label="Название позиции"
          data-testid={`${fieldTestIdPrefix}-name-input`}
          className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card px-3 text-base focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          autoFocus
        />
      </label>

      <div className="flex gap-2">
        <label className="flex flex-col gap-1 flex-1">
          <span className="text-sm font-semibold text-ink">Кол-во</span>
          <input
            type="number"
            value={draft.qty}
            min={0}
            step={0.1}
            onChange={(e) => setDraft({ ...draft, qty: Number(e.target.value) })}
            aria-label="Кол-во"
            data-testid={`${fieldTestIdPrefix}-qty-input`}
            className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card px-3 text-base focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          />
        </label>

        <fieldset className="flex flex-col gap-1 flex-1">
          <legend className="text-sm font-semibold text-ink p-0">Единица</legend>
          <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Единица">
            {CUSTOM_ROW_UNITS.map((u) => {
              const selected = draft.unit === u;
              return (
                <button
                  key={u}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setDraft({ ...draft, unit: u })}
                  data-testid={`${fieldTestIdPrefix}-unit-${u}`}
                  className={
                    'text-sm px-2 py-1 rounded-lg border-[1.5px] border-cta focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
                    (selected ? 'bg-ink text-on-cta' : 'bg-card text-ink')
                  }
                >
                  {u}
                </button>
              );
            })}
          </div>
        </fieldset>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-ink">Цена ₸/{draft.unit}</span>
        <div className="relative">
          <input
            type="number"
            value={draft.price}
            min={0}
            onChange={(e) => setDraft({ ...draft, price: Number(e.target.value) })}
            aria-label="Цена"
            data-testid={`${fieldTestIdPrefix}-price-input`}
            className="w-full min-h-tap rounded-xl border-[1.5px] border-accent bg-card px-3 pr-7 text-base focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          />
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted text-sm pointer-events-none"
            aria-hidden="true"
          >
            ₸
          </span>
        </div>
      </label>

      <div className="flex gap-2 mt-1">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid}
          data-testid={`${fieldTestIdPrefix}-submit-button`}
          className="flex-[2] min-h-tap rounded-xl bg-cta text-on-cta font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed active:opacity-80 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          style={{ minHeight: '56px' }}
        >
          {submitText}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`${fieldTestIdPrefix}-cancel-button`}
          className="flex-1 min-h-tap rounded-xl bg-card border-[1.5px] border-accent text-ink font-medium text-base active:opacity-80 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          style={{ minHeight: '56px' }}
        >
          Отмена
        </button>
      </div>
    </div>
  );
}

export function SectionCustomRows({ rows, onAdd, onUpdate, onRemove }: SectionCustomRowsProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const handleAddSubmit = (draft: DraftState) => {
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const cost = draft.qty * draft.price;
    onAdd({
      id,
      name: draft.name,
      qty: draft.qty,
      qtyAuto: 0,
      qtyOverridden: false,
      unit: draft.unit,
      price: draft.price,
      cost,
      enabled: true,
    });
    setShowAdd(false);
  };

  const handleEditSubmit = (rowId: string, draft: DraftState) => {
    const cost = draft.qty * draft.price;
    onUpdate(rowId, {
      name: draft.name,
      qty: draft.qty,
      unit: draft.unit,
      price: draft.price,
      cost,
    });
    setEditingRowId(null);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="section-custom-rows">
      {/* Card with rows — renders ТОЛЬКО при rows.length > 0 per прототип 2.12.2:527. */}
      {rows.length > 0 ? (
        <SectionCard>
          <SectionHeader>Свои позиции</SectionHeader>
          <div className="flex flex-col gap-2">
            {rows.map((row) => {
              const enabled = row.enabled !== false;
              const isEditing = editingRowId === row.id;

              if (isEditing) {
                // PR-H1 #3 — inline edit form вместо collapsed view.
                return (
                  <div key={row.id} data-testid={`custom-row-${row.id}-editing`} className="py-2">
                    <CustomRowForm
                      initial={rowToDraft(row)}
                      titleText="Редактирование позиции"
                      submitText="Сохранить"
                      formTestId={`custom-row-${row.id}-edit-form`}
                      fieldTestIdPrefix={`custom-row-${row.id}-edit`}
                      onSubmit={(draft) => handleEditSubmit(row.id, draft)}
                      onCancel={() => setEditingRowId(null)}
                    />
                  </div>
                );
              }

              return (
                <div
                  key={row.id}
                  data-testid={`custom-row-${row.id}`}
                  className="flex items-center gap-2 pb-2 border-b border-accent/30 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => onUpdate(row.id, { enabled: e.target.checked })}
                    aria-label={`Включить ${row.name}`}
                    data-testid={`custom-row-${row.id}-toggle`}
                    className="w-5 h-5 accent-cta"
                  />
                  <span
                    className={
                      'flex-1 text-base font-medium ' + (enabled ? 'text-ink' : 'text-muted')
                    }
                  >
                    {row.name}
                  </span>
                  <span className="text-sm text-muted whitespace-nowrap">
                    {row.qty} {row.unit} × {row.price.toLocaleString('ru-RU')} ₸
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingRowId(row.id)}
                    aria-label={`Редактировать ${row.name}`}
                    data-testid={`custom-row-${row.id}-edit-button`}
                    className="min-w-tap min-h-tap rounded-xl text-muted hover:text-ink active:opacity-80 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
                  >
                    <Pencil size={18} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(row.id)}
                    aria-label={`Удалить ${row.name}`}
                    data-testid={`custom-row-${row.id}-delete`}
                    className="min-w-tap min-h-tap rounded-xl text-muted hover:text-error active:opacity-80 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
                  >
                    <Trash2 size={18} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        </SectionCard>
      ) : null}

      {/* Add button OR add form — dashed «+ Добавить позицию» button per прототип 2.12.2:542-545. */}
      <div className="px-3">
        {!showAdd ? (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="custom-row-add-button"
            className="w-full rounded-2xl border-2 border-dashed border-cta bg-card text-ink font-semibold flex items-center justify-center gap-2 active:opacity-80 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
            style={{ minHeight: '56px', fontSize: '18px' }}
          >
            <Plus size={22} aria-hidden="true" />
            Добавить позицию
          </button>
        ) : (
          <CustomRowForm
            initial={INITIAL_DRAFT}
            titleText="Новая позиция"
            submitText="Добавить"
            formTestId="custom-row-add-form"
            fieldTestIdPrefix="custom-row"
            onSubmit={handleAddSubmit}
            onCancel={() => setShowAdd(false)}
          />
        )}
      </div>
    </div>
  );
}

export { CUSTOM_ROW_UNITS };
export type { CustomRowUnit };
